import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getAgentInfo, resumeConversation } from '../api/client';
import { useAgentSession } from '../hooks/useAgentSession';
import { useStreamingSession } from '../hooks/useStreamingSession';
import { AgentConnect } from '../components/AgentConnect';
import { AgentChat } from '../components/AgentChat';
import { SessionMode } from '../types';

type ConnectMode = 'voice' | 'streaming' | 'text';

export function ChatPage() {
  const location = useLocation();
  const initialApiKey = (location.state as any)?.apiKey as string | undefined;
  const [connectMode, setConnectMode] = useState<ConnectMode>('voice');
  const [textOnly, setTextOnly] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const httpSession = useAgentSession();
  const streamSession = useStreamingSession();

  useEffect(() => {
    return () => {
      streamSession.disconnect();
      httpSession.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async (
    apiKey: string,
    mode: SessionMode,
    resumeSessionId?: string,
  ) => {
    setConnectError(null);
    try {
      if (mode === SessionMode.Text) {
        setConnectMode('text');
        setTextOnly(true);
        await httpSession.connect(apiKey, SessionMode.Text, resumeSessionId);
      } else {
        setTextOnly(false);
        const info = await getAgentInfo(apiKey);
        if (info.useStreaming) {
          setConnectMode('streaming');
          // Validate the resume id BEFORE opening the websocket (bad id ->
          // throw -> stay on the connect screen) and seed the prior history.
          const history = resumeSessionId
            ? (await resumeConversation(apiKey, resumeSessionId)).messages.map(
                (m) => ({
                  id: crypto.randomUUID(),
                  role: m.role,
                  text: m.text,
                  timestamp: new Date(m.timestamp),
                }),
              )
            : undefined;
          await streamSession.connect(apiKey, resumeSessionId, history);
        } else {
          setConnectMode('voice');
          await httpSession.connect(apiKey, undefined, resumeSessionId);
        }
      }
    } catch (e: any) {
      setConnectError(e.response?.data?.message || e.message);
    }
  };

  const handleSwitchMode = () => {
    setTextOnly((prev) => !prev);
  };

  const connected =
    connectMode === 'streaming'
      ? !!(streamSession.sessionId && streamSession.agentInfo)
      : !!(httpSession.sessionId && httpSession.agentInfo);

  if (!connected) {
    const activeError =
      connectMode === 'streaming' ? streamSession.error : httpSession.error;
    return (
      <AgentConnect
        error={connectError || activeError}
        initialApiKey={initialApiKey}
        onConnected={handleConnect}
      />
    );
  }

  if (connectMode === 'streaming') {
    return (
      <AgentChat
        agentInfo={streamSession.agentInfo!}
        sessionId={streamSession.sessionId}
        messages={streamSession.messages}
        state={streamSession.state}
        error={streamSession.error}
        onPressStart={() => {}}
        onPressEnd={() => {}}
        onSendText={streamSession.sendTextMessage}
        onClear={streamSession.clearMessages}
        onDisconnect={streamSession.disconnect}
        streamingMode
        muted={streamSession.muted}
        onToggleMute={streamSession.toggleMute}
      />
    );
  }

  return (
    <AgentChat
      agentInfo={httpSession.agentInfo!}
      sessionId={httpSession.sessionId}
      messages={httpSession.messages}
      state={httpSession.state}
      error={httpSession.error}
      onPressStart={httpSession.startVoiceInput}
      onPressEnd={httpSession.stopVoiceInput}
      onSendText={textOnly ? httpSession.sendTextOnly : httpSession.sendTextMessage}
      onClear={httpSession.clearMessages}
      onDisconnect={httpSession.disconnect}
      textOnly={textOnly}
      onSwitchMode={handleSwitchMode}
    />
  );
}
